import type {FirebaseApp} from 'firebase/app'
import type {Auth as FirebaseAuth} from 'firebase/auth'
import type {User} from 'firebase/auth'
import type {Functions} from '$lib/classes/services/functions';
import {goto, replaceState} from '$app/navigation';
// noinspection ES6UnusedImports
import {
    connectAuthEmulator,
    verifyBeforeUpdateEmail,
    deleteUser,
    getAdditionalUserInfo,
    getAuth,
    getRedirectResult,
    GoogleAuthProvider,
    isSignInWithEmailLink,
    ActionCodeURL,
    indexedDBLocalPersistence,
    initializeAuth,
    onAuthStateChanged,
    signInWithEmailAndPassword,
    signInWithCredential,
    signInWithEmailLink,
    signInWithRedirect,
    signOut,
    // updateEmail,
    // connectAuthEmulator
} from 'firebase/auth'
import {differenceInDays} from 'date-fns';
import {FirebaseAuthentication} from '@capacitor-firebase/authentication';
import {logInProduction} from '$lib/utils/logInProduction';
import type {SplashScreenPlugin} from '@capacitor/splash-screen';
import {isFirebaseError} from '$lib/classes/guards/is-firebase-error';
import {getRelativeUrl} from '$lib/utils/get-relative-url';
import type {UserInfo} from '$lib/classes/_types/user-info';

// for emulating the authentication locally start emulation server with firebase emulators:start --only auth
// warning 'WARNING: You are using the Auth Emulator, which is intended for local testing only. Do not use with production credentials.'
// >> 'it means don't type in your real password or anything else sensitive since it's going to be exposed over http when traveling to the emulator.'
// WENN AUTH EMULIERT MUSS AUCH FIRESTORE EMULIERT WERDEN - wegen Rules !!! - Rules? Auth link funktioniert auf jeden Fall nicht...?! Problem in function code?

export class Auth {
    private readonly auth: FirebaseAuth
    private stopAuthStateChangedListenerCallback: (() => void) | null = null

    // public user: User | null = $state(null)

    constructor(
        firebaseApp: FirebaseApp,
        private readonly isNativePlatform: boolean,
        private readonly splashscreen: SplashScreenPlugin,
        private readonly functions: Functions,
        private readonly onNewUser: () => Promise<void>,
        private readonly onLogin: (user: User) => Promise<void>,
        private readonly onLogout: () => Promise<void>,
    ) {
        if (isNativePlatform) {
            this.auth = initializeAuth(firebaseApp, {
                persistence: indexedDBLocalPersistence,
            });
        } else {
            this.auth = getAuth(firebaseApp)
        }

        // To apply the default browser preference instead of explicitly setting it.
        // stand bei Google login - beeinflusst auch Authentication Mail Sprache?
        this.auth.useDeviceLanguage()

        // if (location.hostname === 'localhost') {
        //     connectAuthEmulator(auth, "http://localhost:9099")
        // }
    }

    // public because will be used when handling native 'appUrlOpen' event
    isAuthLink(url: string) {
        return isSignInWithEmailLink(this.auth, url)
    }

    async processSignInWithEmailLink(url: string) {
        const isSignInWithEmailLink = this.isAuthLink(url)
        if (!isSignInWithEmailLink) return
        try {
            let email = window.localStorage.getItem('emailForSignIn') || window.prompt('Please provide your email for confirmation');
            if (!email) return
            const continueUrl = ActionCodeURL.parseLink(url)?.continueUrl;

            const currentUser = await this.getCurrentUserViaAuthStateChangedListener() // auth.currentUser was sometimes null
            logInProduction('currentUser', currentUser)
            if (currentUser) {
                const currentUserEmail = currentUser.email
                if (currentUserEmail === email) {
                    // Already signed in with same account
                    replaceState(window.location.pathname, {}) // clean url, remove auth search params
                    await this.navigateMobile(continueUrl)
                    return // >> finally
                } else {
                    // Already signed in with different account
                    const confirmed = window.confirm(`You are currently signed in with ${currentUserEmail}. You are trying to sign in with a different email address, ${email}. Do you want to switch accounts and proceed?`)
                    if (confirmed) {
                        // Switch account
                        this.stopAuthStateChangeListener() // STOP listener to explicitly await onLogout
                        await this.logout()
                        await this.onLogout() //NOTE if user was set in authStateChange this would be 'skipped' like this !!!
                        //-> continue normal siging flow below
                    } else {
                        // Cancel signing in
                        replaceState(window.location.pathname, {}) // clean url, remove auth search params
                        await this.navigateMobile(continueUrl)
                        return // >> finally
                    }
                }
            }
            // show SplashScreen after prompt/confirm (both kill splashScreen)
            if (this.isNativePlatform) {
                await this.splashscreen.show({autoHide: false})
            }
            this.stopAuthStateChangeListener() // STOP listener to later explicitly await onLogin
            // sign in user
            let credential = await signInWithEmailLink(this.auth, email, url)
            const details = getAdditionalUserInfo(credential)
            if (details?.isNewUser) {
                console.log('+++ NEW USER +++')
                await this.onNewUser()
            }
            replaceState(window.location.pathname, {}) // clean url, remove auth search params
            await this.startAuthStateChangeListener() // START listener again to be able to explicitly await onLogin
            await this.navigateMobile(continueUrl)

        } catch (error: unknown) {
            // TODO optimize error handling with toasts instead of alerts?
            if (isFirebaseError(error)) {
                if (error.code === 'auth/invalid-action-code') {
                    alert('The login link you used is no longer valid, please request a new one.')
                } else {
                    const credential = GoogleAuthProvider.credentialFromError(error);
                    console.error('ERROR processSignInWithEmail - credential ' + credential, error)
                    alert('There was a problem signing you in. Please try again or contact support if the problem persists.')
                }
            } else {
                console.error('!!! Unexpected type of error !!!')
                console.error('ERROR processSignInWithEmail', error)
                alert('There was a problem signing you in. Please try again or contact support if the problem persists.')
            }
        } finally {
            await this.startAuthStateChangeListener() // START listener for desktop AND mobile since onLogout needed to be awaited explicitly on both platforms
            window.localStorage.removeItem('emailForSignIn');
            if (this.isNativePlatform) await this.splashscreen.hide()
        }
    }

    private async navigateMobile(url: string | null | undefined) {
        if (!this.isNativePlatform || !url) return
        await goto(getRelativeUrl(url))
    }

    async processRedirectResult() {
        if (this.isNativePlatform) return //is all handled in signInWithGoogle
        try {
            const credential = await getRedirectResult(this.auth)
            if (!credential) return
            // This gives you a Google Access Token. You can use it to access Google APIs.
            // const OAuthCredential = GoogleAuthProvider.credentialFromResult(redirectResult);
            // const token = OAuthCredential.accessToken;
            const details = getAdditionalUserInfo(credential)
            if (details?.isNewUser) {
                console.log('+++ NEW USER +++')
                await this.onNewUser()
            }
        } catch (error: unknown) {
            if (isFirebaseError(error)) {
                if (error.code === 'auth/user-cancelled') return
                const credential = GoogleAuthProvider.credentialFromError(error);
                console.error('ERROR processRedirectResult - credential ' + credential, error)
                alert('There was a problem signing you in. Please try again or contact support if the problem persists.')
            } else {
                console.error('!!! Unexpected type of error !!!')
                console.error('ERROR processRedirectResult', error)
                alert('There was a problem signing you in. Please try again or contact support if the problem persists.')
            }
        }
    }

    // NOTE start/stopAuthStateChangeListener only necessary for mobile login handling (newUser & redirect)
    async startAuthStateChangeListener(): Promise<void> {
        if (this.stopAuthStateChangedListenerCallback) return
        return new Promise((resolve, reject): void => {
            //NOTE DON'T use native listener >> web auth is important for other services like Firestore
            // and needs to be initialized = user logged in before 'onNewUser/onLogin' callbacks
            this.stopAuthStateChangedListenerCallback = onAuthStateChanged(this.auth, async user => {
                console.log('onAuthStateChanged >>>')
                try {
                    if (user) {
                        console.log('USER', user.uid)
                        await this.onLogin(user)
                    } else {
                        await this.onLogout()
                        console.log('logged OUT')
                    }
                    resolve()
                } catch (error) {
                    reject(error)
                }
            })
        })
    }

    private stopAuthStateChangeListener(): void {
        if (this.stopAuthStateChangedListenerCallback) {
            this.stopAuthStateChangedListenerCallback()
            this.stopAuthStateChangedListenerCallback = null
        }
    }

    async logout() {
        if (this.isNativePlatform) {
            // 1. Sign out on the native layer
            await FirebaseAuthentication.signOut();
        }
        // 1. Sign out on the web layer
        await signOut(this.auth)
    }

    // async signup(email, password) {
    //     let credential = await createUserWithEmailAndPassword(this.auth, email, password)
    //     console.log('signed up', credential);
    //     await USER.createUser()
    // },

    async login(email: string, password: string) {
        let credential = await signInWithEmailAndPassword(this.auth, email, password)
        // console.log('logged in', credential);
    }

    async signInWithEmail(email: string, url: string) {
        // replace localhost on mobile with correct origin to prevent error when link should be opened on desktop with browser
        if (this.isNativePlatform) {
            const urlInstance = new URL(url)
            url = import.meta.env.VITE_ORIGIN + urlInstance.pathname + urlInstance.search + urlInstance.hash
        }
        await this.functions.emailAuthLink({email, url})
        window.localStorage.setItem('emailForSignIn', email);
    }

    async signInWithGoogle(url: string) {
        if (this.isNativePlatform) {
            this.stopAuthStateChangeListener() // STOP listener to later explicitly await onLogin run
            try {
                // 1. Create credentials on the native layer
                const result = await FirebaseAuthentication.signInWithGoogle({
                    mode: 'redirect',
                    scopes: ['https://www.googleapis.com/auth/userinfo.email'],
                });
                await this.splashscreen.show({autoHide: false}) //here since Google dialog closes splashscreen
                // 2. handle newUser
                const newUser = result.additionalUserInfo?.isNewUser //NOTE if newUser only seems to be available from the result
                if (newUser) {
                    logInProduction('+++ NEW USER +++')
                    await this.onNewUser()
                }
                // 3. Sign in on the web layer using the id token
                const credential = GoogleAuthProvider.credential(result.credential?.idToken);
                await signInWithCredential(this.auth, credential);
                await this.startAuthStateChangeListener() // START listener again to explicitly await onLogin
                await this.navigateMobile(url)
            } catch (error) {
                if (error instanceof Error) {
                    //native error from capacitor-firebase > no code
                    if (error.message === 'Authorization canceled.') {
                        return
                    }
                }
                console.error('Error while signing in with redirect', error)
                alert('There was a problem signing you in. Please try again or contact support if the problem persists.')
            } finally {
                await this.startAuthStateChangeListener()
                await this.splashscreen.hide()
            }
        } else {
            // WEB VERSION >>
            const provider = new GoogleAuthProvider();
            // prevent skipping the account selection step on subsequent
            // logins when only one google account is registered
            provider.setCustomParameters({
                prompt: 'select_account'
            });
            try {
                // google will redirect to current url when calling signIn function
                // so if there's a saved url in history state, set it before calling signin
                replaceState(url, {})
                await signInWithRedirect(this.auth, provider)
            } catch (error) {
                // NOTE the 'auth/user-cancelled' error is handled in processRedirectResult >> try-catch here probably redundant
                console.error('Error while signing in with redirect', error)
                alert('There was a problem signing you in. Please try again or contact support if the problem persists.')
            }
        }
    }

    async changeEmail(user: User, newEmail: string) {
        const actionCodeSettings = {
            url: window.location.origin + '/signin?email=' + encodeURIComponent(newEmail),
        }
        await verifyBeforeUpdateEmail(user, newEmail, actionCodeSettings)
        // TODO send email that notifies of requested change with uid and current email --> check if changed and update in mail service
    }

    async deleteAccount() {
        const currentUser = this.auth.currentUser
        if (!currentUser) return
        await deleteUser(this.auth.currentUser)
    }

    async getCurrentUserViaAuthStateChangedListener(): Promise<User | null> {
        return new Promise(resolve => {
            const unsubscribe = onAuthStateChanged(this.auth, user => {
                unsubscribe()
                resolve(user)
            })
        })
    }

    getCurrentUser() {
        return this.auth.currentUser
    }

    getUserEmail() {
        return this.auth.currentUser?.email ?? undefined
    }

    getUid() {
        return this.auth.currentUser?.uid
    }

    get userInfo(): UserInfo | null {
        const currentUser = this.auth.currentUser
        if (!currentUser) {
            console.error('ERROR reading userInfo - no currentUser found')
            return null
        }
        const creationTime = currentUser.metadata.creationTime
        return {
            email: currentUser.email ?? '',
            signInMethod: currentUser.providerData[0].providerId === 'password' ? 'Authentication Link' : 'Google',
            creationTime: creationTime ? new Date(creationTime) : undefined,
        }
    }

    getDaysSignedUp(): number | null {
        const currentUser = this.getCurrentUser()
        if (!currentUser) return null
        const creationTime = currentUser.metadata.creationTime
        if (!creationTime) return null
        return differenceInDays(new Date(), new Date(creationTime))
    }
}

